import { defineStore } from 'pinia'
import type {
    AddonItem,
    CallInfo,
    CardReads,
    CollectorInfo,
    CommunicationType,
    ContactDictionaries,
    DebtInfo,
    DebtShortInfo,
    DebtorEvent,
    DebtorInfo,
    HistoryInfo,
    PaymentInfo,
    ProductInfo,
    ProductsResult,
    StatusInfo,
} from './contracts'

export function createDebtModel(): DebtShortInfo {
    return {
        daysUntilCourt: 0,
        debtCollectionType: '',
        debtId: '',
        loanAgreementNumber: '',
        sellerName: '',
        totalDebtAmount: 0,
    }
}

export function createDebtorModel(): DebtorInfo {
    return {
        addons: { items: [] },
        addresses: { residential: null, registered: null, additional: [] },
        contacts: {
            emails: {
                additional: [],
                main: null,
                mainContactType: null,
                mainContactTypeLabel: { en: '', es: '', ru: '' },
            },
            phones: {
                additional: [],
                main: null,
                mainContactType: null,
                mainContactTypeLabel: { en: '', es: '', ru: '' },
            },
        },
        debtorAssignment: null,
        documents: { passport: null, socialInsurance: null, taxNumber: null },
        financialSummary: { debts: [], lastCommunication: null, totalDebt: 0 },
        jobs: [],
        messengers: {},
        offerStatus: null,
        person: {
            birthDate: '',
            birthPlace: null,
            firstName: '',
            gender: '',
            lastName: '',
            patronymic: null,
            uuid: '',
        },
        personalInfo: { children: null, maritalStatus: null },
        refinanceOffer: false,
        refinanceOfferAt: null,
        registers: [],
        statusFlags: {
            bankrupt: false,
            creditHoliday: false,
            courtProcess: false,
            debtorDeceased: false,
            disabilityGroup1: false,
            disabilityGroup2: false,
            disabilityGroup3: false,
            enforcementProceeding: false,
            initiallyBankrupt: false,
            initiallyDataIssue: false,
            initiallyPotentialBankrupt: false,
            noContactThirdParties: false,
            prison: false,
            refusedContact: false,
        },
        timezoneMsk: null,
    }
}

export function createCardReads(): CardReads {
    const read = () => ({
        personId: null,
        pending: false,
        hasData: false,
        error: null,
        revision: 0,
    })
    const paged = () => ({
        ...read(),
        acceptedQuery: { limit: 15, offset: 0 },
        requestedQuery: { limit: 15, offset: 0 },
    })
    return {
        fullInfo: read(),
        debts: read(),
        payments: read(),
        history: paged(),
        activity: paged(),
        calls: paged(),
        products: read(),
        collectors: read(),
    }
}

interface DebtorCardState {
    accessDenied: boolean
    calls: CallInfo[]
    collectors: CollectorInfo[]
    commTypes: CommunicationType[]
    currentDebt: DebtShortInfo
    currentDebts: DebtInfo[] | null
    currentDebtor: DebtorInfo
    currentEvents: DebtorEvent[]
    currentHistory: HistoryInfo[]
    currentPayments: PaymentInfo[] | null
    currentProducts: {
        active: ProductInfo[]
        terminated: ProductInfo[]
        totalActual: number
        totalTerminated: number
    }
    enums: ContactDictionaries
    revokingAddonId: string | null
    reads: CardReads
    statuses: StatusInfo[]
    totalEventsLen: number
    totalHistoryLen: number
}

export const useDebtorCardStore = defineStore('fsd-debtor-card', {
    state: (): DebtorCardState => ({
        accessDenied: false,
        calls: [],
        collectors: [],
        commTypes: [],
        currentDebt: createDebtModel(),
        currentDebts: [],
        currentDebtor: createDebtorModel(),
        currentEvents: [],
        currentHistory: [],
        currentPayments: [],
        currentProducts: { active: [], terminated: [], totalActual: 0, totalTerminated: 0 },
        enums: {
            contactRelation: [],
            contactSource: [],
            contactTypeEmail: [],
            contactTypePhone: [],
        },
        revokingAddonId: null,
        reads: createCardReads(),
        statuses: [],
        totalEventsLen: 0,
        totalHistoryLen: 0,
    }),
    actions: {
        applyAddon(item: AddonItem): void {
            const replace = (wrapper?: { items: AddonItem[] } | null): void => {
                const index = wrapper?.items.findIndex((addon) => addon.id === item.id) ?? -1
                if (wrapper && index !== -1) wrapper.items.splice(index, 1, item)
            }
            replace(this.currentDebtor.addons)
            this.currentDebtor.financialSummary.debts.forEach((debt) => replace(debt.addons))
        },
        clearCard(): void {
            this.accessDenied = false
            this.currentDebt = createDebtModel()
            this.currentDebts = []
            this.currentDebtor = createDebtorModel()
            this.currentEvents = []
            this.currentHistory = []
            this.currentPayments = []
            this.currentProducts = {
                active: [],
                terminated: [],
                totalActual: 0,
                totalTerminated: 0,
            }
            this.calls = []
            this.reads = createCardReads()
            this.totalEventsLen = 0
            this.totalHistoryLen = 0
        },
        setContactDictionaries(dictionaries: ContactDictionaries): void {
            this.enums = dictionaries
        },
        setCurrentDebt(debt: DebtShortInfo): void {
            this.currentDebt = debt
        },
        setDebtor(debtor: DebtorInfo): void {
            this.currentDebtor = debtor
            this.currentDebt = debtor.financialSummary.debts[0] || createDebtModel()
        },
        setProducts(products: ProductsResult): void {
            this.currentProducts = {
                active: products.actualProducts,
                terminated: products.terminatedProducts,
                totalActual: products.totalActualProductCount,
                totalTerminated: products.totalTerminatedProductCount,
            }
        },
        setStatuses(statuses: StatusInfo[]): void {
            this.statuses = statuses
                .filter((status) => status.canUse && status.category !== 'technical')
                .sort((a, b) => a.weight - b.weight)
        },
    },
})
